import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Search,
  MapPin,
  Mail,
  Instagram,
} from "lucide-react";
import NewsTicker from './NewsTicker';
import { useManagedCollection, slugify } from '../admin/useManagedCollection';
import { AdminItem } from '../admin/AdminControls';
import type { FieldDef } from '../admin/AdminFieldModal';

// Editable fields for a category, matching the homepage "Explore Categories"
// grid. AdminItem gives View All the same drag-reorder / edit / hide controls,
// and since catMc uses the same "home_categories" store, changes sync both ways.
const CATEGORY_FIELDS: FieldDef[] = [
  { key: "name", label: "Category name", type: "text", required: true },
  { key: "href", label: "Link (path, e.g. /restaurants)", type: "text" },
];

// ─── Same icons from HomeSections ───
function RestaurantsIcon({ className }: { className?: string }) {
  return (
    <img
      src="https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1780340164199-6yn4hj.png"
      alt="Restaurants"
      className={className}
      style={{ objectFit: "contain" }}
    />
  );
}

function ConcertsIcon({ className }: { className?: string }) {
  return (
    <img
      src="https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1780338859810-lpw44o.png"
      alt="Concerts/Events"
      className={className}
      style={{ objectFit: "contain" }}
    />
  );
}

function TemplesIcon({ className }: { className?: string }) {
  return (
    <img
      src="https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1780338860170-7my59.png"
      alt="Temples"
      className={className}
      style={{ objectFit: "contain" }}
    />
  );
}

function ShoppingIcon({ className }: { className?: string }) {
  return (
    <img
      src="https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1780340164009-pppzhi.png"
      alt="Shopping"
      className={className}
      style={{ objectFit: "contain" }}
    />
  );
}

function LawyersIcon({ className }: { className?: string }) {
  return (
    <img
      src="https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1780340164180-31av45.png"
      alt="Lawyers"
      className={className}
      style={{ objectFit: "contain" }}
    />
  );
}

function HealthWellnessIcon({ className }: { className?: string }) {
  return (
    <img
      src="https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1780340319237-4x8nm.png"
      alt="Health & Wellness"
      className={className}
      style={{ objectFit: "contain" }}
    />
  );
}

function InfluencersIcon({ className }: { className?: string }) {
  return (
    <img
      src="https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1780338859925-bo6iq.png"
      alt="Influencers"
      className={className}
      style={{ objectFit: "contain" }}
    />
  );
}

function ModelsIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      <circle cx="24" cy="14" r="7" fill="#f59e0b" opacity="0.85"/>
      <path d="M24 22 C15 22 10 28 10 38 L10 40 L38 40 L38 38 C38 28 33 22 24 22 Z" fill="#082f6d" opacity="0.85"/>
      <path d="M17 26 L24 40 L31 26" stroke="#51ba4f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    </svg>
  );
}

// ─── New categories: inline colorful SVG icons (no placeholder images) ───
function MoviesIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      <rect x="8" y="20" width="32" height="20" rx="3" fill="#8B5CF6" opacity="0.15"/>
      <rect x="8" y="20" width="32" height="20" rx="3" stroke="#8B5CF6" strokeWidth="2"/>
      <path d="M8 20 L10.5 13 L40 13 L37.5 20 Z" fill="#8B5CF6" opacity="0.3" stroke="#8B5CF6" strokeWidth="2" strokeLinejoin="round"/>
      <path d="M16 13 L13.5 20 M24 13 L21.5 20 M32 13 L29.5 20" stroke="#8B5CF6" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}

function RealtorsIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      <path d="M11 23 L24 11 L37 23 L37 38 L11 38 Z" fill="#10B981" opacity="0.15"/>
      <path d="M7 24 L24 10 L41 24" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M11 22 V38 H37 V22" stroke="#10B981" strokeWidth="2" strokeLinejoin="round"/>
      <rect x="21" y="30" width="6" height="8" rx="0.5" fill="#10B981"/>
    </svg>
  );
}

function VenuesIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      <path d="M8 18 L24 9 L40 18 Z" fill="#0891b2" opacity="0.15" />
      <path d="M6 18 L24 9 L42 18" stroke="#0891b2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <rect x="11" y="18" width="3" height="18" fill="#0891b2" opacity="0.55" />
      <rect x="18.5" y="18" width="3" height="18" fill="#0891b2" opacity="0.55" />
      <rect x="26.5" y="18" width="3" height="18" fill="#0891b2" opacity="0.55" />
      <rect x="34" y="18" width="3" height="18" fill="#0891b2" opacity="0.55" />
      <rect x="8" y="36" width="32" height="3" rx="1" fill="#0891b2" />
    </svg>
  );
}

function PhotoVideoIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      {/* Camera body */}
      <rect x="6" y="16" width="26" height="20" rx="3" fill="#EC4899" opacity="0.15"/>
      <rect x="6" y="16" width="26" height="20" rx="3" stroke="#EC4899" strokeWidth="2"/>
      {/* Camera bump */}
      <rect x="14" y="12" width="7" height="4" rx="1.5" fill="#EC4899" opacity="0.5" stroke="#EC4899" strokeWidth="1.5"/>
      {/* Lens */}
      <circle cx="19" cy="26" r="6" stroke="#EC4899" strokeWidth="2"/>
      <circle cx="19" cy="26" r="3" fill="#EC4899" opacity="0.4"/>
      {/* Video play arrow */}
      <path d="M35 19 L44 24 L35 29 Z" fill="#EC4899" opacity="0.8" stroke="#EC4899" strokeWidth="1.5" strokeLinejoin="round"/>
      <line x1="33" y1="19" x2="33" y2="29" stroke="#EC4899" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  );
}

function EventDecorIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      {/* Balloon 1 */}
      <ellipse cx="14" cy="19" rx="6" ry="8" fill="#F97316" opacity="0.85"/>
      <path d="M14 27 Q13 30 14 32" stroke="#F97316" strokeWidth="1.5" strokeLinecap="round"/>
      {/* Balloon 2 */}
      <ellipse cx="26" cy="17" rx="6" ry="8" fill="#8B5CF6" opacity="0.85"/>
      <path d="M26 25 Q25 28 26 30" stroke="#8B5CF6" strokeWidth="1.5" strokeLinecap="round"/>
      {/* Balloon 3 */}
      <ellipse cx="37" cy="20" rx="5" ry="7" fill="#EC4899" opacity="0.85"/>
      <path d="M37 27 Q36 30 37 32" stroke="#EC4899" strokeWidth="1.5" strokeLinecap="round"/>
      {/* Star */}
      <path d="M7 9 L8 12 L11 13 L8 14 L7 17 L6 14 L3 13 L6 12 Z" fill="#F59E0B"/>
      {/* String */}
      <path d="M14 32 Q20 37 26 30 Q32 36 37 32" stroke="#9CA3AF" strokeWidth="1.2" strokeLinecap="round" fill="none" strokeDasharray="2 2"/>
    </svg>
  );
}

function GroceryIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      {/* Cart handle */}
      <path d="M6 10 H11 L15 28 H36 L40 16 H14" stroke="#16A34A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
      {/* Cart body fill */}
      <path d="M15 28 H36 L40 16 H14 Z" fill="#16A34A" opacity="0.12"/>
      {/* Wheels */}
      <circle cx="19" cy="33" r="2.5" fill="#16A34A" opacity="0.7" stroke="#16A34A" strokeWidth="1.5"/>
      <circle cx="32" cy="33" r="2.5" fill="#16A34A" opacity="0.7" stroke="#16A34A" strokeWidth="1.5"/>
      {/* Produce */}
      <circle cx="21" cy="23" r="2.5" fill="#F59E0B" opacity="0.9"/>
      <circle cx="28" cy="22" r="2.5" fill="#EF4444" opacity="0.9"/>
      <circle cx="34" cy="23" r="2" fill="#16A34A" opacity="0.9"/>
    </svg>
  );
}

function SweetTemptationsIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      {/* Ice cream scoop */}
      <circle cx="24" cy="18" r="10" fill="#F9A8D4" opacity="0.9"/>
      <circle cx="24" cy="18" r="10" stroke="#EC4899" strokeWidth="1.5"/>
      {/* Cone */}
      <path d="M16 26 L24 42 L32 26 Z" fill="#F59E0B" opacity="0.85"/>
      <path d="M16 26 L24 42 L32 26 Z" stroke="#D97706" strokeWidth="1.5" strokeLinejoin="round"/>
      {/* Cone lines */}
      <line x1="20" y1="27" x2="24" y2="40" stroke="#D97706" strokeWidth="0.8" strokeLinecap="round"/>
      <line x1="28" y1="27" x2="24" y2="40" stroke="#D97706" strokeWidth="0.8" strokeLinecap="round"/>
      {/* Cherry on top */}
      <circle cx="24" cy="9" r="2.5" fill="#EF4444"/>
      <path d="M24 9 Q27 5 30 7" stroke="#16A34A" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
      {/* Sprinkles */}
      <rect x="19" y="15" width="3" height="1.2" rx="0.6" fill="#8B5CF6" transform="rotate(-30 19 15)"/>
      <rect x="25" y="19" width="3" height="1.2" rx="0.6" fill="#F97316" transform="rotate(20 25 19)"/>
      <rect x="21" y="21" width="3" height="1.2" rx="0.6" fill="#06B6D4" transform="rotate(-10 21 21)"/>
    </svg>
  );
}

function CafeIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      {/* Coffee cup */}
      <path d="M10 18 L12 38 Q12 42 16 42 L28 42 Q32 42 32 38 L34 18 Z" fill="#F59E0B" opacity="0.85"/>
      <path d="M10 18 L12 38 Q12 42 16 42 L28 42 Q32 42 32 38 L34 18 Z" stroke="#D97706" strokeWidth="1.5" strokeLinejoin="round"/>
      {/* Handle */}
      <path d="M34 22 Q42 22 41 29 Q40 35 33 34" stroke="#D97706" strokeWidth="2.5" strokeLinecap="round" fill="none"/>
      {/* Coffee surface */}
      <ellipse cx="22" cy="18" rx="12" ry="2.5" fill="#78350F" opacity="0.7"/>
      {/* Steam */}
      <path d="M16 8 Q14 12 16 14" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" fill="none" opacity="0.6"/>
      <path d="M22 6 Q20 10 22 13" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" fill="none" opacity="0.6"/>
      <path d="M28 8 Q26 12 28 14" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" fill="none" opacity="0.6"/>
      {/* Saucer */}
      <ellipse cx="22" cy="44" rx="16" ry="2" fill="#D1D5DB"/>
    </svg>
  );
}

function EventOrganizerIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      <rect x="12" y="16" width="16" height="14" rx="3" fill="#9CA3AF" opacity="0.2"/>
      <path d="M28 18 L38 12 L38 36 L28 30 Z" fill="#9CA3AF" opacity="0.2" stroke="#9CA3AF" strokeWidth="1.5" strokeLinejoin="round"/>
      <rect x="12" y="20" width="7" height="6" rx="1" fill="#9CA3AF" opacity="0.15"/>
      <path d="M32 18 Q36 23 32 30" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" fill="none" opacity="0.25"/>
      <path d="M34 16 Q40 23 34 32" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" fill="none" opacity="0.18"/>
      <circle cx="17" cy="23" r="2" fill="#9CA3AF" opacity="0.2"/>
    </svg>
  );
}

function MakeupHairIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      {/* Mirror */}
      <ellipse cx="18" cy="18" rx="8" ry="10" fill="#F9A8D4" opacity="0.35"/>
      <ellipse cx="18" cy="18" rx="8" ry="10" stroke="#EC4899" strokeWidth="1.5"/>
      <ellipse cx="18" cy="18" rx="5" ry="7" fill="#FDF2F8" stroke="#EC4899" strokeWidth="0.8"/>
      {/* Mirror handle */}
      <rect x="16" y="28" width="4" height="8" rx="1.5" fill="#EC4899" opacity="0.7"/>
      {/* Lipstick */}
      <rect x="32" y="28" width="5" height="12" rx="2" fill="#EF4444" opacity="0.9"/>
      <rect x="31.5" y="26" width="6" height="4" rx="1.5" fill="#EA580C"/>
      <rect x="31" y="24.5" width="7" height="2.5" rx="1" fill="#FBBF24"/>
      {/* Hair brush */}
      <rect x="33" y="8" width="8" height="4" rx="2" fill="#8B5CF6" opacity="0.8" transform="rotate(-20 37 10)"/>
      <rect x="35" y="6" width="10" height="3" rx="1.5" fill="#A78BFA" opacity="0.7" transform="rotate(-20 40 7.5)"/>
      {/* Sparkles */}
      <path d="M6 6 L7 9 L10 10 L7 11 L6 14 L5 11 L2 10 L5 9 Z" fill="#F59E0B"/>
      <path d="M42 16 L42.5 17.5 L44 18 L42.5 18.5 L42 20 L41.5 18.5 L40 18 L41.5 17.5 Z" fill="#FBBF24"/>
    </svg>
  );
}

function SalonSpaIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      {/* Lotus/flower */}
      <path d="M24 8 Q28 14 24 20 Q20 14 24 8 Z" fill="#EC4899" opacity="0.6"/>
      <path d="M24 8 Q30 14 24 20 Q18 14 24 8 Z" fill="#F472B6" opacity="0.5"/>
      <path d="M24 8 Q32 16 24 22 Q16 16 24 8 Z" fill="#FBCFE8" opacity="0.4"/>
      <circle cx="24" cy="14" r="2.5" fill="#F59E0B"/>
      {/* Steam / aroma lines */}
      <path d="M9 22 Q9 16 13 12" stroke="#A78BFA" strokeWidth="1.5" strokeLinecap="round" fill="none" opacity="0.6"/>
      <path d="M12 24 Q12 18 15 14" stroke="#A78BFA" strokeWidth="1.2" strokeLinecap="round" fill="none" opacity="0.4"/>
      <path d="M39 22 Q39 16 35 12" stroke="#A78BFA" strokeWidth="1.5" strokeLinecap="round" fill="none" opacity="0.6"/>
      <path d="M36 24 Q36 18 33 14" stroke="#A78BFA" strokeWidth="1.2" strokeLinecap="round" fill="none" opacity="0.4"/>
      {/* Towel roll */}
      <rect x="14" y="32" width="20" height="10" rx="3" fill="#E5E7EB" stroke="#D1D5DB" strokeWidth="1"/>
      <ellipse cx="24" cy="32" rx="10" ry="2.5" fill="#F3F4F6" stroke="#D1D5DB" strokeWidth="0.8"/>
      {/* Stones */}
      <ellipse cx="18" cy="34" rx="2" ry="1.2" fill="#9CA3AF" opacity="0.5"/>
      <ellipse cx="24" cy="35" rx="2.5" ry="1.2" fill="#9CA3AF" opacity="0.5"/>
      <ellipse cx="30" cy="34" rx="2" ry="1.2" fill="#9CA3AF" opacity="0.5"/>
    </svg>
  );
}

function DJsIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      {/* Headphones */}
      <path d="M14 30 Q14 22 24 18 Q34 22 34 30" stroke="#8B5CF6" strokeWidth="2.5" strokeLinecap="round" fill="none"/>
      <rect x="10" y="28" width="8" height="6" rx="3" fill="#8B5CF6" opacity="0.8"/>
      <rect x="30" y="28" width="8" height="6" rx="3" fill="#8B5CF6" opacity="0.8"/>
      {/* Headband */}
      <path d="M14 30 Q14 20 24 14 Q34 20 34 30" stroke="#8B5CF6" strokeWidth="2.5" strokeLinecap="round" fill="none" opacity="0.5"/>
      {/* Turntable / vinyl */}
      <circle cx="24" cy="34" r="8" fill="#1F2937" opacity="0.85"/>
      <circle cx="24" cy="34" r="3" fill="#F97316" opacity="0.9"/>
      <circle cx="24" cy="34" r="1" fill="#1F2937"/>
      {/* Tonearm */}
      <path d="M27 27 Q32 24 34 28" stroke="#E5E7EB" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
      {/* Equalizer bars */}
      <rect x="6" y="26" width="2.5" height="6" rx="1" fill="#F59E0B" opacity="0.9"/>
      <rect x="9.5" y="23" width="2.5" height="9" rx="1" fill="#F97316" opacity="0.9"/>
      <rect x="13" y="20" width="2.5" height="12" rx="1" fill="#EF4444" opacity="0.9"/>
      <rect x="34" y="24" width="2.5" height="8" rx="1" fill="#F59E0B" opacity="0.9"/>
      <rect x="37.5" y="21" width="2.5" height="11" rx="1" fill="#F97316" opacity="0.9"/>
      <rect x="41" y="26" width="2.5" height="6" rx="1" fill="#EF4444" opacity="0.9"/>
    </svg>
  );
}

function KidsIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      <circle cx="24" cy="16" r="8" fill="#FCD34D" opacity="0.9" stroke="#F59E0B" strokeWidth="1.5"/>
      <circle cx="20" cy="14" r="1.5" fill="#1F2937"/>
      <circle cx="28" cy="14" r="1.5" fill="#1F2937"/>
      <path d="M20 19 Q24 23 28 19" stroke="#1F2937" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
      <rect x="18" y="24" width="12" height="14" rx="3" fill="#60A5FA" opacity="0.8" stroke="#3B82F6" strokeWidth="1.5"/>
      <rect x="14" y="26" width="6" height="8" rx="2" fill="#F472B6" opacity="0.8" stroke="#EC4899" strokeWidth="1"/>
      <rect x="28" y="26" width="6" height="8" rx="2" fill="#F472B6" opacity="0.8" stroke="#EC4899" strokeWidth="1"/>
      <circle cx="24" cy="32" r="2" fill="#F59E0B"/>
    </svg>
  );
}

function SeniorCitizensIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      <circle cx="24" cy="14" r="7" fill="#FCD34D" opacity="0.9" stroke="#F59E0B" strokeWidth="1.5"/>
      <circle cx="21" cy="12" r="1.2" fill="#1F2937"/>
      <circle cx="27" cy="12" r="1.2" fill="#1F2937"/>
      <path d="M21 17 Q24 20 27 17" stroke="#1F2937" strokeWidth="1.2" strokeLinecap="round" fill="none"/>
      <path d="M16 22 Q24 16 32 22" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
      <rect x="18" y="24" width="12" height="14" rx="3" fill="#60A5FA" opacity="0.7" stroke="#3B82F6" strokeWidth="1.5"/>
      <path d="M14 36 L14 40 M34 36 L34 40" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round"/>
      <rect x="12" y="38" width="6" height="2" rx="1" fill="#9CA3AF" opacity="0.6"/>
      <rect x="30" y="38" width="6" height="2" rx="1" fill="#9CA3AF" opacity="0.6"/>
    </svg>
  );
}

function StartUpsIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      {/* Rocket body */}
      <path d="M24 6 L18 22 L24 28 L30 22 Z" fill="#0891b2" opacity="0.85" stroke="#0e7490" strokeWidth="1.5" strokeLinejoin="round"/>
      {/* Rocket nose */}
      <path d="M24 6 L22 10 L26 10 Z" fill="#06b6d4" opacity="0.9"/>
      {/* Window */}
      <circle cx="24" cy="16" r="2.5" fill="#cffafe" stroke="#0e7490" strokeWidth="1"/>
      {/* Fins */}
      <path d="M18 22 L12 28 L18 26 Z" fill="#06b6d4" opacity="0.8" stroke="#0e7490" strokeWidth="1" strokeLinejoin="round"/>
      <path d="M30 22 L36 28 L30 26 Z" fill="#06b6d4" opacity="0.8" stroke="#0e7490" strokeWidth="1" strokeLinejoin="round"/>
      {/* Flame */}
      <path d="M21 28 Q24 38 27 28" fill="#f97316" opacity="0.9"/>
      <path d="M22.5 28 Q24 34 25.5 28" fill="#fbbf24" opacity="0.9"/>
      {/* Stars */}
      <path d="M8 8 L9 11 L12 12 L9 13 L8 16 L7 13 L4 12 L7 11 Z" fill="#f59e0b"/>
      <path d="M40 10 L40.8 12.2 L43 13 L40.8 13.8 L40 16 L39.2 13.8 L37 13 L39.2 12.2 Z" fill="#fbbf24"/>
    </svg>
  );
}

function DatingMatrimonialIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      <path d="M24 38 C12 28 6 22 6 16 C6 11 10 8 14 8 C17 8 20 10 24 14 C28 10 31 8 34 8 C38 8 42 11 42 16 C42 22 36 28 24 38 Z" fill="#EC4899" opacity="0.85" stroke="#BE185D" strokeWidth="1.5" strokeLinejoin="round"/>
      <path d="M24 38 C12 28 6 22 6 16 C6 11 10 8 14 8 C17 8 20 10 24 14" fill="#F472B6" opacity="0.6"/>
    </svg>
  );
}

function TailoringIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      {/* Scissors */}
      <circle cx="12" cy="30" r="4" fill="#DB2777" opacity="0.15" stroke="#DB2777" strokeWidth="1.5"/>
      <circle cx="12" cy="16" r="4" fill="#DB2777" opacity="0.15" stroke="#DB2777" strokeWidth="1.5"/>
      <path d="M15 18 L34 32" stroke="#DB2777" strokeWidth="2" strokeLinecap="round"/>
      <path d="M15 28 L34 14" stroke="#DB2777" strokeWidth="2" strokeLinecap="round"/>
      {/* Thread spool */}
      <circle cx="36" cy="12" r="5" fill="#F59E0B" opacity="0.15" stroke="#F59E0B" strokeWidth="1.5"/>
      <path d="M36 8 L36 16 M32 12 L40 12" stroke="#F59E0B" strokeWidth="1.2" strokeLinecap="round"/>
      {/* Needle & thread */}
      <path d="M20 38 Q28 34 36 38" stroke="#8B5CF6" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
      <circle cx="20" cy="38" r="1.5" fill="#8B5CF6"/>
      <circle cx="36" cy="38" r="1.5" fill="#8B5CF6"/>
    </svg>
  );
}

function AutomotiveServicesIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      {/* Car body */}
      <path d="M6 30 L9 20 Q10 17 14 17 L34 17 Q38 17 39 20 L42 30" stroke="#082f6d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="#082f6d" fillOpacity="0.12"/>
      <rect x="4" y="30" width="40" height="7" rx="2.5" fill="#082f6d" opacity="0.85"/>
      {/* Wheels */}
      <circle cx="14" cy="38" r="4" fill="#1f2937" stroke="#082f6d" strokeWidth="1.2"/>
      <circle cx="34" cy="38" r="4" fill="#1f2937" stroke="#082f6d" strokeWidth="1.2"/>
      {/* Wrench */}
      <path d="M30 8 L38 16" stroke="#f59e0b" strokeWidth="2.4" strokeLinecap="round"/>
      <circle cx="29" cy="7" r="4" fill="none" stroke="#f59e0b" strokeWidth="2"/>
    </svg>
  );
}

function CricketIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      {/* Cricket ball */}
      <circle cx="16" cy="28" r="7" fill="#EF4444" opacity="0.9" stroke="#B91C1C" strokeWidth="1.5"/>
      <path d="M16 21 Q16 28 16 35" stroke="#B91C1C" strokeWidth="1.2" strokeLinecap="round"/>
      <path d="M11 25 Q16 28 21 25" stroke="#B91C1C" strokeWidth="1.2" strokeLinecap="round"/>
      <path d="M11 31 Q16 28 21 31" stroke="#B91C1C" strokeWidth="1.2" strokeLinecap="round"/>
      {/* Cricket bat */}
      <rect x="28" y="8" width="4" height="22" rx="1.5" fill="#D97706" opacity="0.9"/>
      <path d="M26 8 L34 8 L32 4 L28 4 Z" fill="#F59E0B" opacity="0.9" stroke="#D97706" strokeWidth="1"/>
      <rect x="26" y="30" width="8" height="3" rx="1" fill="#D97706" opacity="0.8"/>
      {/* Wickets/stumps */}
      <rect x="36" y="32" width="1.5" height="10" rx="0.75" fill="#92400E" opacity="0.7"/>
      <rect x="39" y="32" width="1.5" height="10" rx="0.75" fill="#92400E" opacity="0.7"/>
      <rect x="42" y="32" width="1.5" height="10" rx="0.75" fill="#92400E" opacity="0.7"/>
      <rect x="35" y="30" width="10" height="1.5" rx="0.75" fill="#92400E" opacity="0.5"/>
    </svg>
  );
}

function OthersIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      <circle cx="16" cy="16" r="6" fill="#8B5CF6" opacity="0.8"/>
      <circle cx="32" cy="16" r="6" fill="#EC4899" opacity="0.8"/>
      <circle cx="24" cy="32" r="6" fill="#F97316" opacity="0.8"/>
      <circle cx="16" cy="16" r="2" fill="#C4B5FD"/>
      <circle cx="32" cy="16" r="2" fill="#FBCFE8"/>
      <circle cx="24" cy="32" r="2" fill="#FED7AA"/>
    </svg>
  );
}

function BartendersIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      <path d="M16 8 L32 8 L28 24 L20 24 Z" fill="#06B6D4" opacity="0.15" stroke="#06B6D4" strokeWidth="2" strokeLinejoin="round"/>
      <ellipse cx="24" cy="8" rx="8" ry="2" fill="#06B6D4" opacity="0.5"/>
      <rect x="20" y="24" width="8" height="2" rx="1" fill="#06B6D4" opacity="0.6"/>
      <rect x="22" y="26" width="4" height="10" rx="1" fill="#06B6D4" opacity="0.4"/>
      <ellipse cx="24" cy="38" rx="6" ry="2" fill="#06B6D4" opacity="0.25"/>
      <circle cx="18" cy="16" r="2" fill="#F59E0B" opacity="0.8"/>
      <circle cx="30" cy="14" r="1.5" fill="#EF4444" opacity="0.7"/>
    </svg>
  );
}

function BakersIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      <path d="M16 36 Q16 24 24 20 Q32 24 32 36 Z" fill="#F59E0B" opacity="0.85" stroke="#D97706" strokeWidth="1.5"/>
      <ellipse cx="24" cy="18" rx="10" ry="4" fill="#FCD34D" opacity="0.9" stroke="#F59E0B" strokeWidth="1.5"/>
      <circle cx="20" cy="16" r="1.5" fill="#F59E0B"/>
      <circle cx="24" cy="15" r="1.5" fill="#F59E0B"/>
      <circle cx="28" cy="16" r="1.5" fill="#F59E0B"/>
      <path d="M14 36 Q14 30 18 28" stroke="#D97706" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
      <path d="M34 36 Q34 30 30 28" stroke="#D97706" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
    </svg>
  );
}

function CateringOnlyIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      <ellipse cx="24" cy="22" rx="16" ry="3" fill="#8B5CF6" opacity="0.2"/>
      <ellipse cx="24" cy="20" rx="14" ry="8" fill="#8B5CF6" opacity="0.15" stroke="#8B5CF6" strokeWidth="2"/>
      <ellipse cx="24" cy="20" rx="10" ry="5" fill="#C4B5FD" opacity="0.5"/>
      <circle cx="18" cy="18" r="2.5" fill="#F97316" opacity="0.8"/>
      <circle cx="24" cy="17" r="2.5" fill="#EF4444" opacity="0.8"/>
      <circle cx="30" cy="18" r="2.5" fill="#16A34A" opacity="0.8"/>
      <rect x="20" y="28" width="8" height="2" rx="1" fill="#8B5CF6" opacity="0.5"/>
    </svg>
  );
}

function HomeServicesIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      <path d="M8 22 L24 8 L40 22" stroke="#10B981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M12 22 V38 H36 V22" stroke="#10B981" strokeWidth="2" strokeLinejoin="round" fill="#10B981" opacity="0.1"/>
      <rect x="21" y="30" width="6" height="8" rx="1" fill="#10B981" opacity="0.7"/>
      <path d="M30 14 L34 10 L38 14" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
      <circle cx="34" cy="10" r="1.5" fill="#F59E0B"/>
    </svg>
  );
}

function TravelAgentsIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      <path d="M8 34 L16 22 L24 28 L32 18 L40 30" stroke="#EC4899" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
      <circle cx="16" cy="22" r="2" fill="#EC4899" opacity="0.6"/>
      <circle cx="32" cy="18" r="2" fill="#EC4899" opacity="0.6"/>
      <path d="M36 10 L40 6 L44 10 L40 14 Z" fill="#F97316" opacity="0.8" stroke="#EA580C" strokeWidth="1.5" strokeLinejoin="round"/>
      <path d="M40 14 L40 20" stroke="#EA580C" strokeWidth="1.5" strokeLinecap="round"/>
      <path d="M36 18 L44 18" stroke="#EA580C" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}

function PerformingArtistsIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      <circle cx="16" cy="18" r="8" fill="#8B5CF6" opacity="0.15" stroke="#8B5CF6" strokeWidth="2"/>
      <circle cx="32" cy="18" r="8" fill="#EC4899" opacity="0.15" stroke="#EC4899" strokeWidth="2"/>
      <circle cx="16" cy="18" r="2.5" fill="#8B5CF6" opacity="0.7"/>
      <circle cx="32" cy="18" r="2.5" fill="#EC4899" opacity="0.7"/>
      <path d="M12 26 Q16 30 20 26" stroke="#8B5CF6" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
      <path d="M28 26 Q32 30 36 26" stroke="#EC4899" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
      <path d="M16 26 L16 34" stroke="#8B5CF6" strokeWidth="1.5" strokeLinecap="round"/>
      <path d="M32 26 L32 34" stroke="#EC4899" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}

function MediaIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      <rect x="6" y="14" width="28" height="22" rx="3" fill="#F97316" opacity="0.15" stroke="#F97316" strokeWidth="2"/>
      <rect x="12" y="10" width="8" height="4" rx="1.5" fill="#F97316" opacity="0.5" stroke="#F97316" strokeWidth="1.5"/>
      <circle cx="20" cy="25" r="7" stroke="#F97316" strokeWidth="2"/>
      <circle cx="20" cy="25" r="3" fill="#F97316" opacity="0.4"/>
      <path d="M36 18 L44 24 L36 30 Z" fill="#F97316" opacity="0.7" stroke="#F97316" strokeWidth="1.5" strokeLinejoin="round"/>
      <line x1="34" y1="18" x2="34" y2="30" stroke="#F97316" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  );
}

function NonProfitOrgIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      <circle cx="24" cy="24" r="18" fill="#14B8A6" opacity="0.12"/>
      <path d="M24 36 C14 28 8 22 8 16 C8 12 12 10 16 10 C19 10 22 12 24 15 C26 12 29 10 32 10 C36 10 40 12 40 16 C40 22 34 28 24 36 Z" fill="#14B8A6" opacity="0.8" stroke="#0D9488" strokeWidth="1.5" strokeLinejoin="round"/>
      <path d="M24 36 C14 28 8 22 8 16 C8 12 12 10 16 10 C19 10 22 12 24 15" fill="#5EEAD4" opacity="0.5"/>
      <circle cx="16" cy="16" r="2" fill="#F59E0B" opacity="0.8"/>
      <circle cx="32" cy="16" r="2" fill="#F59E0B" opacity="0.8"/>
    </svg>
  );
}

function YouthIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      <circle cx="24" cy="24" r="18" fill="#3B82F6" opacity="0.1"/>
      <path d="M24 6 L27 17 L38 17 L29 24 L32 35 L24 28 L16 35 L19 24 L10 17 L21 17 Z" fill="#3B82F6" opacity="0.8" stroke="#2563EB" strokeWidth="1.5" strokeLinejoin="round"/>
      <circle cx="24" cy="22" r="3" fill="#FBBF24" opacity="0.9"/>
    </svg>
  );
}

function WomenIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      <circle cx="24" cy="24" r="18" fill="#EC4899" opacity="0.1"/>
      <circle cx="16" cy="14" r="5" fill="#EC4899" opacity="0.7" stroke="#BE185D" strokeWidth="1.5"/>
      <circle cx="32" cy="14" r="5" fill="#F472B6" opacity="0.7" stroke="#BE185D" strokeWidth="1.5"/>
      <path d="M16 19 L16 28 L24 36 L32 28 L32 19" stroke="#EC4899" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="#EC4899" opacity="0.15"/>
      <circle cx="16" cy="14" r="1.5" fill="#FDF2F8"/>
      <circle cx="32" cy="14" r="1.5" fill="#FDF2F8"/>
    </svg>
  );
}

function KaraokeSpotsIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      <rect x="20" y="8" width="8" height="16" rx="4" fill="#8B5CF6" opacity="0.2" stroke="#8B5CF6" strokeWidth="2"/>
      <ellipse cx="24" cy="8" rx="5" ry="3" fill="#8B5CF6" opacity="0.3" stroke="#8B5CF6" strokeWidth="1.5"/>
      <line x1="24" y1="24" x2="24" y2="34" stroke="#8B5CF6" strokeWidth="2" strokeLinecap="round"/>
      <line x1="16" y1="34" x2="32" y2="34" stroke="#8B5CF6" strokeWidth="2.5" strokeLinecap="round"/>
      <path d="M34 14 L34 26 M38 12 L38 24" stroke="#A78BFA" strokeWidth="1.8" strokeLinecap="round"/>
      <circle cx="32" cy="26" r="2" fill="#A78BFA" opacity="0.7"/>
      <circle cx="36" cy="24" r="2" fill="#A78BFA" opacity="0.7"/>
    </svg>
  );
}

function FinancialServicesIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      <circle cx="24" cy="24" r="18" fill="#082f6d" opacity="0.1"/>
      <circle cx="24" cy="24" r="10" stroke="#082f6d" strokeWidth="2"/>
      <path d="M24 18 V30 M20.5 27 Q20.5 30 24 30 Q27.5 30 27.5 27.5 Q27.5 25 24 24 Q20.5 23 20.5 20.5 Q20.5 18 24 18 Q27.5 18 27.5 21" stroke="#082f6d" strokeWidth="1.8" strokeLinecap="round" fill="none"/>
      <path d="M10 34 L14 28 L19 31 L24 22 L30 27 L38 16" stroke="#51ba4f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
      <path d="M32 16 L38 16 L38 22" stroke="#51ba4f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    </svg>
  );
}

function InsuranceIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      <circle cx="24" cy="24" r="18" fill="#f59e0b" opacity="0.1"/>
      <path d="M24 8 L36 13 V23 C36 31 30.5 36.5 24 39 C17.5 36.5 12 31 12 23 V13 Z" stroke="#082f6d" strokeWidth="2" strokeLinejoin="round" fill="none"/>
      <path d="M18 24 L22.5 28.5 L31 19" stroke="#51ba4f" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    </svg>
  );
}

function JobsIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      {/* Briefcase body */}
      <rect x="8" y="16" width="32" height="22" rx="3" fill="#082f6d" opacity="0.12"/>
      <rect x="8" y="16" width="32" height="22" rx="3" stroke="#082f6d" strokeWidth="2"/>
      {/* Handle */}
      <path d="M18 16 V13 C18 11.9 18.9 11 20 11 H28 C29.1 11 30 11.9 30 13 V16" stroke="#082f6d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
      {/* Divider seam */}
      <line x1="8" y1="25" x2="40" y2="25" stroke="#082f6d" strokeWidth="1.5"/>
      {/* Clasps */}
      <rect x="21" y="22" width="6" height="6" rx="1" fill="#f59e0b"/>
      <rect x="21" y="22" width="6" height="6" rx="1" stroke="#082f6d" strokeWidth="1.2"/>
      {/* Sparkle accent */}
      <path d="M38 8 L38.7 10.3 L41 11 L38.7 11.7 L38 14 L37.3 11.7 L35 11 L37.3 10.3 Z" fill="#51ba4f"/>
    </svg>
  );
}

const categories = [
  { name: "Restaurants", icon: RestaurantsIcon, active: true, href: "/restaurants" },
  { name: "Financial Services", icon: FinancialServicesIcon, active: false, href: "/financial-services" },
  { name: "Insurance", icon: InsuranceIcon, active: false, href: "/insurance" },
  { name: "Concerts/Events", icon: ConcertsIcon, active: false, href: "/concerts" },
  { name: "Temples", icon: TemplesIcon, active: false, href: "/temples" },
  { name: "Party Venues", icon: VenuesIcon, active: false, href: "/party-venues" },
  { name: "Movies", icon: MoviesIcon, active: false, href: "/movies" },
  { name: "Shopping", icon: ShoppingIcon, active: false, href: "/shopping" },
  { name: "Realtors", icon: RealtorsIcon, active: false, href: "/realtors" },
  { name: "Influencers", icon: InfluencersIcon, active: false, href: "/influencers" },
  { name: "Models", icon: ModelsIcon, active: false, href: "/models" },
  { name: "Photo/Video", icon: PhotoVideoIcon, active: false, href: "/photo-video" },
  { name: "Event Planning", icon: EventDecorIcon, active: false, href: "/event-management" },
  { name: "Marketing/production", icon: EventOrganizerIcon, active: false, href: "/event-production" },
  { name: "Grocery", icon: GroceryIcon, active: false, href: "/grocery" },
  { name: "Dessert Shops", icon: SweetTemptationsIcon, active: false, href: "/dessert-shops" },
  { name: "Cafe", icon: CafeIcon, active: false, href: "/cafe" },
  // Food Trucks removed — now merged into Restaurants page
  { name: "Lawyers", icon: LawyersIcon, active: false, href: "/lawyers" },
  { name: "Health & Wellness", icon: HealthWellnessIcon, active: false, href: "/health-wellness" },
  // Makeup removed — merged into Beauty Services
  // Hookah Lounges removed — now merged into Restaurants page
  { name: "Beauty Services", icon: MakeupHairIcon, active: false, href: "/BeautyServices" },
  { name: "Salon/Spa", icon: SalonSpaIcon, active: false, href: "/salon-spa" },
  { name: "DJ's", icon: DJsIcon, active: false, href: "/djs" },
  { name: "Kids", icon: KidsIcon, active: false, href: "/kids" },
  { name: "Catering", icon: CateringOnlyIcon, active: false, href: "/catering-only" },
  { name: "Home Services", icon: HomeServicesIcon, active: false, href: "/home-services" },
  { name: "Travel Agents", icon: TravelAgentsIcon, active: false, href: "/travel-agents" },
  { name: "Performing Artists", icon: PerformingArtistsIcon, active: false, href: "/performing-artists" },
  { name: "Media", icon: MediaIcon, active: false, href: "/media" },
  { name: "Senior Citizens", icon: SeniorCitizensIcon, active: false, href: "/senior-citizen" },
  { name: "Start Ups", icon: StartUpsIcon, active: false, href: "/start-ups" },
  { name: "Dating/Matrimonial", icon: DatingMatrimonialIcon, active: false, href: "/dating-matrimonial" },
  { name: "Non Profit Org", icon: NonProfitOrgIcon, active: false, href: "/non-profit-org" },
  { name: "Youth", icon: YouthIcon, active: false, href: "/youth" },
  { name: "Women", icon: WomenIcon, active: false, href: "/women" },
  { name: "Karaoke Spots", icon: KaraokeSpotsIcon, active: false, href: "/karaoke-spots" },
  { name: "Cricket", icon: CricketIcon, active: false, href: "/cricket" },
  { name: "Tailoring", icon: TailoringIcon, active: false, href: "/tailoring" },
  { name: "Automotive Services", icon: AutomotiveServicesIcon, active: false, href: "/automotive-services" },
  { name: "Jobs", icon: JobsIcon, active: false, href: "/jobs" },
  { name: "Others", icon: OthersIcon, active: false, href: "/others" },
];

const trendingItems = [
  "Restaurants",
  "Lawyers",
  "Health & Wellness",
];

const HERO_LOCATIONS = [
  "All Locations",
  "Frisco",
  "Plano",
  "Irving",
  "Lewisville",
  "McKinney",
  "Dallas",
  "Aubrey",
];

function CategoryCard({
  category,
  index,
}: {
  category: (typeof categories)[0];
  index: number;
}) {
  const [isHovered, setIsHovered] = useState(false);
  const Icon = category.icon;

  return (
    <Link to={category.href} className="block">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-40px" }}
        transition={{ duration: 0.4, delay: index * 0.04 }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className="relative flex flex-col items-center bg-white rounded-[10px] border border-[#e9edf3] hover:shadow-md transition-all duration-300 group"
        style={{
          fontFamily: "'Poppins', sans-serif",
          minHeight: "76px",
          padding: "6px 4px 4px",
          boxShadow: "0 2px 8px rgba(15, 23, 42, 0.08)",
          gap: "1px",
        }}
      >
        {'noIcon' in category && category.noIcon ? (
          <div className="absolute inset-0 flex items-center justify-center overflow-hidden rounded-[10px] opacity-25 pointer-events-none">
            <Icon className="w-[56px] h-[56px]" />
          </div>
        ) : (
          <div className="flex items-center justify-center transition-transform duration-300 group-hover:scale-110 flex-shrink-0">
            <Icon className="w-[40px] h-[40px]" />
          </div>
        )}
        <span
          className="font-bold leading-[1.15] text-center transition-colors duration-300 overflow-hidden text-ellipsis"
          style={{
            color: "#111827",
            maxWidth: "100%",
            fontSize: ('noIcon' in category && category.noIcon) ? '8px' : '10px',
            ...(('noIcon' in category && category.noIcon) ? { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', position: 'relative', zIndex: 1 } : {}),
          }}
        >
          {'noIcon' in category && category.noIcon
            ? category.name.split('/').map((word, i) => <span key={i} className="block leading-[1.3]">{word.trim()}</span>)
            : category.name === "Concerts/Events" ? <><span className="block leading-[1.3]">Concerts/</span><span className="block leading-[1.3]">Events</span></> : category.name === "Marketing/production" ? <><span className="block leading-[1.3]">Marketing/</span><span className="block leading-[1.3]">production</span></> : category.name
          }
        </span>
        {(category.active || isHovered) && (
          <div
            className="absolute left-1/2 -translate-x-1/2 rounded-full bg-[#f97316]"
            style={{ bottom: "-1px", width: "40px", height: "2px" }}
          />
        )}
      </motion.div>
    </Link>
  );
}

// InstagramWidget removed — only on home page

export default function ViewAllCategories() {
  const [searchQuery, setSearchQuery] = useState("");
  const [email, setEmail] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("All Locations");
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Same store slug as HomeSections ("home_categories") so hide/edit/reorder
  // changes made from the home page are reflected here too, and vice versa.
  const catMc = useManagedCollection<(typeof categories)[number]>(
    "home_categories",
    categories,
    (c) => slugify(c.name)
  );

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    }
    if (showDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showDropdown]);

  return (
    <div className="min-h-screen bg-white">
      {/* ─── Hero Section (same as homepage) ─── */}
      <section
        className="relative w-full bg-cover bg-center"
        style={{
          backgroundImage:
            'url("https://storage.googleapis.com/happyspace-generated-images/generated/2026-06-21/38c0a4b3b01c.jpeg")',
          minHeight: "160px",
        }}
      >
        <div className="absolute inset-0 bg-black/20" />

        <div className="relative z-10 max-w-[1320px] w-[calc(100%-40px)] mx-auto px-4 pt-3 sm:pt-4 pb-8 flex flex-col lg:flex-row items-start justify-between gap-4 lg:gap-6">
          <div className="flex-1 flex flex-col items-center text-center w-full min-w-0">
            <motion.h1
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-white text-[22px] sm:text-[25px] md:text-[27px] leading-tight tracking-tight drop-shadow-sm"
              style={{ fontFamily: "'Poppins', sans-serif", textShadow: "0 1px 3px rgba(0,0,0,0.4)" }}
            >
              <span className="font-normal">Connecting </span>
              <span className="font-extrabold">Desi's</span>
              <span className="font-normal"> in </span>
              <span className="font-extrabold">Dallas–DFW</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="mt-1 text-white text-[13px] sm:text-[15px] md:text-[16px] font-semibold"
              style={{ fontFamily: "'Poppins', sans-serif", textShadow: "0 1px 2px rgba(0,0,0,0.3)" }}
            >
              <span className="font-normal">Discover </span>
              <span className="text-[#fbbf24] font-bold">
                Businesses, Events, Reviews & Stories
              </span>
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="mt-3 w-full max-w-[740px]"
            >
              <div
                className="rounded-md"
                style={{
                  boxShadow: "0 0 40px 12px rgba(255,255,255,0.35), 0 0 80px 24px rgba(255,255,255,0.15)",
                  backdropFilter: "blur(4px)",
                }}
              >
                <div className="flex flex-col sm:flex-row bg-white rounded-md shadow-md overflow-hidden h-auto sm:h-[40px]">
                  <div className="flex-1 px-3 py-2 sm:py-0 flex items-center border-b sm:border-b-0 sm:border-r border-gray-200 min-h-[40px]">
                    <span className="text-[11px] text-gray-600 font-semibold shrink-0 mr-2 hidden sm:inline">
                      What are you looking for?
                    </span>
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="e.g. Best Restaurants, Immigration Lawyer"
                      className="w-full text-[12px] text-gray-700 placeholder:text-gray-400 outline-none bg-transparent"
                    />
                  </div>

                  <div
                    ref={dropdownRef}
                    className="relative border-b sm:border-b-0 sm:border-r border-gray-200 min-h-[40px] sm:w-[170px] md:w-[190px] shrink-0"
                    style={{ zIndex: 99999 }}
                  >
                    <button
                      type="button"
                      onClick={() => setShowDropdown(!showDropdown)}
                      className="w-full h-full px-3 py-2 sm:py-0 flex items-center gap-1.5 text-left min-h-[40px]"
                    >
                      <MapPin className="w-3.5 h-3.5 text-gray-500 shrink-0" />
                      <span className="text-[12px] text-gray-700 font-medium truncate flex-1">{selectedLocation}</span>
                      <svg
                        className={"w-3 h-3 text-gray-400 shrink-0 transition-transform " + (showDropdown ? "rotate-180" : "")}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {showDropdown && (
                      <div
                        className="absolute top-full left-0 bg-white border border-gray-100 rounded-lg py-1 overflow-y-auto"
                        style={{
                          zIndex: 99999,
                          marginTop: "4px",
                          width: "200px",
                          maxHeight: "220px",
                          boxShadow: "0 10px 24px rgba(15,23,42,0.16)",
                          fontFamily: "'Poppins', sans-serif",
                        }}
                      >
                        {HERO_LOCATIONS.map((loc) => (
                          <button
                            key={loc}
                            type="button"
                            onClick={() => { setSelectedLocation(loc); setShowDropdown(false); }}
                            className="w-full text-left flex items-center transition-colors duration-150"
                            style={{
                              padding: "8px 12px",
                              fontSize: "13px",
                              fontWeight: selectedLocation === loc ? 700 : 500,
                              color: selectedLocation === loc ? "#f97316" : "#111827",
                              background: selectedLocation === loc ? "#fff3e8" : "transparent",
                              height: "36px",
                            }}
                            onMouseEnter={(e) => { if (selectedLocation !== loc) (e.currentTarget as HTMLButtonElement).style.background = "#fff7ed"; }}
                            onMouseLeave={(e) => { if (selectedLocation !== loc) (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
                          >
                            {loc}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <button className="bg-[#f97316] hover:bg-[#ea580c] text-white text-[13px] font-bold px-5 py-2.5 sm:py-0 transition-colors duration-200 shrink-0 min-h-[40px] sm:rounded-r-md w-full sm:w-auto">
                    <span className="flex items-center justify-center gap-1.5">
                      <Search className="w-3.5 h-3.5 sm:hidden" />
                      Search
                    </span>
                  </button>
                </div>
              </div>
            </motion.div>

          </div>

          <motion.div
            initial={{ opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.25 }}
            className="hidden lg:flex flex-col bg-white/95 backdrop-blur-sm rounded-lg shadow-lg p-3 w-full lg:w-[260px] shrink-0 self-start mt-1"
          >
            <h3 className="text-[14px] font-bold text-gray-800 mb-1" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Stay in the Scoop
            </h3>
            <p className="text-[11px] text-gray-500 mb-2">
              Monthly Highlights
            </p>
            <div className="flex items-center gap-2">
              <div className="flex-1 flex items-center bg-gray-50 rounded-md px-2.5 py-1.5 border border-gray-200 min-w-0">
                <Mail className="w-3.5 h-3.5 text-gray-400 mr-1.5 shrink-0" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your Email"
                  className="w-full text-[12px] text-gray-700 placeholder:text-gray-400 outline-none bg-transparent min-w-0"
                />
              </div>
              <button className="bg-[#f97316] hover:bg-[#ea580c] text-white text-[12px] font-bold px-3 py-1.5 rounded-md transition-colors duration-200 shrink-0">
                Subscribe
              </button>
            </div>
          </motion.div>
        </div>

        <svg
          className="hero-curve"
          viewBox="0 0 1440 24"
          preserveAspectRatio="none"
          style={{
            position: "absolute",
            left: 0,
            bottom: "-1px",
            width: "100%",
            height: "18px",
            display: "block",
            zIndex: 3,
          }}
        >
          <path d="M0,0 Q720,24 1440,0 L1440,24 L0,24 Z" fill="#ffffff" />
        </svg>
      </section>
      <NewsTicker />

      {/* ─── Explore Categories Section ─── */}
      <section className="bg-gray-50/50 py-6">
        <div className="max-w-[1320px] w-[calc(100%-40px)] mx-auto px-4">
          <div className="flex flex-col lg:flex-row gap-6">
            {/* LEFT: Categories */}
            <div className="flex-1 min-w-0">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5 }}
                className="mb-6"
              >
                <div className="flex items-center justify-between mb-3">
                  <h2
                    className="text-[16px] md:text-[18px] font-bold text-gray-900"
                    style={{ fontFamily: "'Poppins', sans-serif" }}
                  >
                    Explore Categories
                  </h2>
                </div>
                <div className="category-grid">
                  {[...catMc.items]
                    .sort((a, b) => {
                      const aLast = a.name === "Others" ? 1 : 0;
                      const bLast = b.name === "Others" ? 1 : 0;
                      return aLast - bLast;
                    })
                    .map((cat, i) => (
                      <AdminItem key={cat._key} mc={catMc} item={cat} fields={CATEGORY_FIELDS} title="Category">
                        <CategoryCard category={cat as any} index={i} />
                      </AdminItem>
                    ))}
                </div>
              </motion.div>
            </div>

            {/* Instagram widget removed — only on home page */}
          </div>
        </div>
      </section>
    </div>
  );
}
